/**
 * Feature Flags Hook
 * 
 * Provides access to server-side feature flags with caching and error handling
 */

import { useState, useEffect, useCallback, useContext, createContext } from 'react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '';

// Feature flags context
const FeatureFlagsContext = createContext({
  flags: {},
  loading: false,
  error: null,
  refetch: () => {},
  isFeatureEnabled: () => false
});

/**
 * Default feature flags (used as fallback)
 */
const DEFAULT_FLAGS = {
  SMUGMUG_ENABLED: false,
  FLICKR_ENABLED: true,
  UNSPLASH_ENABLED: true,
  BATCH_PROCESSING_V2: false,
  ADVANCED_AI_STYLES: false,
  METADATA_PREVIEW: true
};

/**
 * Feature Flags Provider Component
 */
export const FeatureFlagsProvider = ({ children }) => {
  const [flags, setFlags] = useState(DEFAULT_FLAGS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch feature flags from server
  const fetchFeatureFlags = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await axios.get(`${API_BASE}/api/features`, {
        timeout: 5000, // 5 second timeout
        withCredentials: true
      });

      if (response.data && response.data.success && response.data.flags) {
        setFlags({ ...DEFAULT_FLAGS, ...response.data.flags });
        console.log('✅ Feature flags loaded:', response.data.flags);
      } else {
        throw new Error('Invalid feature flags response format');
      }
    } catch (err) {
      console.warn('⚠️ Failed to load feature flags, using defaults:', err.message);
      setError(err.message);
      setFlags(DEFAULT_FLAGS); // Fallback to defaults
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize feature flags on mount
  useEffect(() => {
    fetchFeatureFlags();
  }, [fetchFeatureFlags]);

  // Helper function to check if a feature is enabled
  const isFeatureEnabled = useCallback((featureKey) => {
    return Boolean(flags[featureKey]);
  }, [flags]);

  const value = {
    flags,
    loading,
    error,
    refetch: fetchFeatureFlags,
    isFeatureEnabled
  };

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
};

/**
 * Hook to use feature flags
 */
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagsContext);
  
  if (context === undefined) {
    throw new Error('useFeatureFlags must be used within a FeatureFlagsProvider');
  }
  
  return context;
};

/**
 * Hook to check if a specific feature is enabled
 */
export const useFeatureEnabled = (featureKey) => {
  const { isFeatureEnabled } = useFeatureFlags();
  return isFeatureEnabled(featureKey);
};

/**
 * Hook for provider-specific feature flags
 */
export const useProviderFeatures = () => {
  const { flags } = useFeatureFlags();
  
  return {
    isSmugMugEnabled: Boolean(flags.SMUGMUG_ENABLED),
    isFlickrEnabled: Boolean(flags.FLICKR_ENABLED),
    isUnsplashEnabled: Boolean(flags.UNSPLASH_ENABLED),
    enabledProviders: [
      flags.FLICKR_ENABLED && 'flickr',
      flags.UNSPLASH_ENABLED && 'unsplash', 
      flags.SMUGMUG_ENABLED && 'smugmug'
    ].filter(Boolean)
  };
};

/**
 * Higher-order component to conditionally render based on feature flags
 */
export const withFeatureFlag = (featureKey, fallback = null) => (Component) => {
  return (props) => {
    const isEnabled = useFeatureEnabled(featureKey);
    
    if (!isEnabled) {
      return fallback;
    }
    
    return <Component {...props} />;
  };
};

/**
 * Component to conditionally render children based on feature flag
 */
export const FeatureFlag = ({ feature, fallback = null, children }) => {
  const isEnabled = useFeatureEnabled(feature);
  
  if (!isEnabled) {
    return fallback;
  }
  
  return children;
};
